/**
 * Public Landing Page — Lumina edition
 * Dark UI, glassmorphism, glowing gradients, cinematic core.
 * Premium, minimal, hierarchy-focused.
 *
 * Palette: #F1F3F8 (base) · #8B5CF6 (rust accent) · #5E73C1 (royal blue accent)
 * · #0E0E0E (text) · #5A575B (text-soft)
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  ArrowRight, Brain, Sparkle,
  Plus, ChartLineUp, Crosshair, MagnifyingGlass, FileText, ShieldCheck,
  Check, X, List, Pulse, Trophy,
  Rocket, GridFour, Star, Target, FilePdf, Cube,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// ATOMS
// ============================================================
export const LuminaLogo = ({ size = 72, className = "", testid }) => (
  <img
    src="/brand/prime-pillar-icon.png"
    alt="Prime Pillar"
    width={size}
    height={size}
    draggable={false}
    data-testid={testid}
    className={`object-contain select-none pointer-events-none ${className}`}
    style={{ width: size, height: size }}
  />
);

// Full wordmark: "Prime" (dark navy) + "Pillar" (violet) — used in the app
// header and landing page header in place of plain text.
export const PrimePillarWordmark = ({ height = 32, className = "", testid }) => (
  <img
    src="/brand/prime-pillar-wordmark.png"
    alt="Prime Pillar"
    height={height}
    draggable={false}
    data-testid={testid}
    className={`object-contain select-none pointer-events-none ${className}`}
    style={{ height, width: "auto" }}
  />
);
// ============================================================
const Eyebrow = ({ children, tone = "rust" }) => {
  const map = {
    rust: "text-[#6D45D6] border-[#8B5CF6]/40 bg-[#8B5CF6]/12",
    royal: "text-[#3F4E8A] border-[#5E73C1]/50 bg-[#5E73C1]/15",
    mute: "text-[#3F3D40] border-[#0E0E0E]/15 bg-white/70",
  };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border backdrop-blur-md font-mono-data text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold ${map[tone]}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-90" />
      {children}
    </span>
  );
};

const GradHeading = ({ children, className = "" }) => (
  <h2 className={`font-display tracking-[-0.03em] leading-[0.95] text-[#0E0E0E] ${className}`}>
    {children}
  </h2>
);

const PillBtn = ({ children, onClick, variant = "primary", testid }) => {
  const base = "inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm transition-all duration-200 group";
  const v = {
    primary: "bg-[#8B5CF6] text-white hover:bg-[#7C4DEC] lum-glow-rust hover:shadow-[0_16px_50px_-8px_rgba(139,92,246,0.55)]",
    ghost: "bg-white/5 text-[#0E0E0E] border border-white/15 hover:bg-white/10 hover:border-white/30 backdrop-blur-md",
    royal: "bg-[#5E73C1] text-white hover:bg-[#4E63B1] lum-glow-royal",
  }[variant];
  return (
    <button onClick={onClick} className={`${base} ${v}`} data-testid={testid}>
      {children}
    </button>
  );
};

// ============================================================
// NAV
// ============================================================
const LandingNav = ({ onSignIn, onSignUp, onDiscover, onEvaluate, onBestCountry, onGuide }) => {
  const [open, setOpen] = useState(false);
  const go = (fn) => { setOpen(false); fn && fn(); };
  return (
  <>
  <nav className="fixed top-0 left-0 right-0 z-40 lum-glass border-b border-white/8" data-testid="landing-nav">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="flex items-center gap-2.5 group bg-transparent border-0 p-0 cursor-pointer flex-shrink-0 mr-4"
        data-testid="landing-logo-link"
      >
        <LuminaLogo size={48} className="transition-transform group-hover:scale-105" testid="landing-logo" />
        <PrimePillarWordmark height={32} className="hidden sm:block" testid="landing-wordmark" />
        <span className="sm:hidden font-display text-base tracking-tight text-[#0E0E0E]">Prime Pillar</span>
      </button>
      <div className="hidden xl:flex items-center gap-1 text-sm font-medium text-[#5A575B]">
        {/* Proven Winners family */}
        <button
          type="button"
          onClick={onDiscover}
          className="px-3 py-2 rounded-full whitespace-nowrap text-[#8B5CF6] font-semibold hover:bg-[#8B5CF6]/8 transition-colors bg-transparent border-0 cursor-pointer"
          data-testid="landing-nav-discover"
        >
          Discover
        </button>
        <button
          type="button"
          onClick={onEvaluate}
          className="px-3 py-2 rounded-full whitespace-nowrap text-[#8B5CF6] font-semibold hover:bg-[#8B5CF6]/8 transition-colors bg-transparent border-0 cursor-pointer"
          data-testid="landing-nav-evaluate"
        >
          Evaluate
        </button>
        <span className="mx-1 h-4 w-px bg-[#E5E8F0]" />
        {/* Demand Signals family */}
        <a href="#trending" className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors" data-testid="landing-nav-signals">Signals</a>
        <a href="#trending" className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors" data-testid="landing-nav-trending">Trending</a>
        <button type="button" onClick={onBestCountry} className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors bg-transparent border-0 cursor-pointer font-medium" data-testid="landing-nav-best-country">Best Country</button>
        <span className="mx-1 h-4 w-px bg-[#E5E8F0]" />
        <a href="#how-it-works" className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors" data-testid="landing-nav-how">How it works</a>
        <button type="button" onClick={onGuide} className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors bg-transparent border-0 cursor-pointer font-medium" data-testid="landing-nav-guide">Guide</button>
        <a href="#vs-competition" className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors" data-testid="landing-nav-vs">Compare</a>
        <a href="#pricing" className="px-3 py-2 rounded-full whitespace-nowrap hover:text-[#0E0E0E] hover:bg-[#F1F3F8] transition-colors" data-testid="landing-nav-pricing">Pricing</a>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={onSignIn} className="hidden xl:inline-flex px-4 py-2 text-sm font-medium whitespace-nowrap text-[#5A575B] hover:text-[#0E0E0E] transition-colors" data-testid="landing-nav-signin">Sign in</button>
        <button onClick={onDiscover} className="hidden sm:inline-flex px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap text-white bg-[#8B5CF6] hover:bg-[#7C4DEC] transition-all lum-glow-rust" data-testid="landing-nav-getstarted">
          Enter →
        </button>
        {/* Mobile hamburger */}
        <button onClick={() => setOpen(true)} className="xl:hidden min-h-[44px] min-w-[44px] flex items-center justify-center text-[#0E0E0E]" data-testid="landing-nav-hamburger" aria-label="Open menu">
          <List size={26} weight="bold" />
        </button>
      </div>
    </div>
  </nav>

    {/* Mobile menu drawer */}
    {open && (
      <div className="xl:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="landing-mobile-overlay">
        <div className="absolute inset-y-0 right-0 w-[88%] max-w-sm bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 h-16 border-b border-[#E5E8F0] flex-shrink-0">
            <span className="font-display text-base text-[#0E0E0E]">Prime Pillar</span>
            <button onClick={() => setOpen(false)} className="-mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-[#5A575B]" data-testid="landing-mobile-close" aria-label="Close menu">
              <X size={22} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">
            <div className="px-2 text-[10px] font-mono-data uppercase tracking-[0.18em] text-[#9B9498] mb-1">Proven Winners</div>
            <button onClick={() => go(onDiscover)} className="w-full text-left px-3 min-h-[48px] rounded-xl text-[15px] font-semibold text-[#8B5CF6] hover:bg-[#F1F3F8]" data-testid="landing-mobile-discover">Discover</button>
            <button onClick={() => go(onEvaluate)} className="w-full text-left px-3 min-h-[48px] rounded-xl text-[15px] font-semibold text-[#8B5CF6] hover:bg-[#F1F3F8]" data-testid="landing-mobile-evaluate">Evaluate</button>
            <div className="px-2 pt-4 text-[10px] font-mono-data uppercase tracking-[0.18em] text-[#9B9498] mb-1">Demand Signals</div>
            <a href="#trending" onClick={() => setOpen(false)} className="block px-3 leading-[48px] rounded-xl text-[15px] font-medium text-[#0E0E0E] hover:bg-[#F1F3F8]" data-testid="landing-mobile-signals">Signals</a>
            <a href="#trending" onClick={() => setOpen(false)} className="block px-3 leading-[48px] rounded-xl text-[15px] font-medium text-[#0E0E0E] hover:bg-[#F1F3F8]" data-testid="landing-mobile-trending">Trending</a>
            <button onClick={() => go(onBestCountry)} className="w-full text-left px-3 min-h-[48px] rounded-xl text-[15px] font-medium text-[#0E0E0E] hover:bg-[#F1F3F8]" data-testid="landing-mobile-best-country">Best Country</button>
            <div className="h-px bg-[#E5E8F0] my-3" />
            <a href="#how-it-works" onClick={() => setOpen(false)} className="block px-3 leading-[48px] rounded-xl text-[15px] text-[#5A575B] hover:bg-[#F1F3F8]">How it works</a>
            <button onClick={() => go(onGuide)} className="w-full text-left px-3 min-h-[48px] rounded-xl text-[15px] font-medium text-[#0E0E0E] hover:bg-[#F1F3F8]" data-testid="landing-mobile-guide">Guide &amp; Demo</button>
            <a href="#vs-competition" onClick={() => setOpen(false)} className="block px-3 leading-[48px] rounded-xl text-[15px] text-[#5A575B] hover:bg-[#F1F3F8]">Compare</a>
            <a href="#pricing" onClick={() => setOpen(false)} className="block px-3 leading-[48px] rounded-xl text-[15px] text-[#5A575B] hover:bg-[#F1F3F8]">Pricing</a>
          </div>
          <div className="px-4 py-4 border-t border-[#E5E8F0] space-y-2 flex-shrink-0">
            <button onClick={() => go(onSignIn)} className="w-full px-4 min-h-[48px] rounded-full text-sm font-medium text-[#0E0E0E] border border-[#E5E8F0]" data-testid="landing-mobile-signin">Sign in</button>
            <button onClick={() => go(onDiscover)} className="w-full px-4 min-h-[48px] rounded-full text-sm font-semibold text-white bg-[#8B5CF6]" data-testid="landing-mobile-getstarted">Enter →</button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

// ============================================================
// HERO — Calyro-style 2-column: bold headline + live scanner mockup
// ============================================================
const HeroSection = ({ onPrimaryPath, onSecondaryPath }) => {
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-32 pb-8 sm:pb-16 px-4 sm:px-8" data-testid="hero-section">
      {/* Soft grid pattern background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
        backgroundImage: "linear-gradient(rgba(94,115,193,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(94,115,193,0.08) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage: "radial-gradient(ellipse 80% 70% at 50% 40%, #000 30%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 40%, #000 30%, transparent 90%)",
      }} />
      {/* Soft color washes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[700px] rounded-full bg-[#8B5CF6]/10 blur-[140px]" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#5E73C1]/12 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#B5C8E5]/30 blur-[110px]" />
      </div>

      <div className="relative max-w-5xl mx-auto text-center">
        {/* Eyebrow with icon */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#E5E8F0] shadow-sm text-[13px] sm:text-sm text-[#5A575B] font-medium max-w-full" data-testid="hero-eyebrow">
          <ShieldCheck size={15} weight="duotone" className="text-[#8B5CF6] flex-shrink-0" />
          <span className="whitespace-nowrap sm:whitespace-normal">Two evidence-backed paths<span className="hidden sm:inline"> · pick your starting point</span></span>
        </div>

        {/* Big headline */}
        <h1 className="mt-6 sm:mt-7 font-display tracking-[-0.035em] leading-[1.06] text-[#0E0E0E] text-[26px] xs:text-[30px] sm:text-[46px] lg:text-[58px] font-bold max-w-4xl mx-auto" data-testid="hero-headline">
          Find your{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(110deg, #5E73C1 0%, #8B5CF6 45%, #C084FC 100%)" }}
          >
            next startup
          </span>{" "}
          — backed by real demand, not guesses.
        </h1>

        {/* Body */}
        <p className="mt-5 sm:mt-6 max-w-2xl mx-auto text-[#3F3D40] text-[15px] sm:text-[18px] leading-relaxed">
          <span className="font-semibold text-[#0E0E0E]">Prime Pillar</span> finds{" "}
          <span className="font-semibold text-[#0E0E0E]">proven business models worth cloning into new markets</span>, AND surfaces{" "}
          <span className="font-semibold text-[#0E0E0E]">raw demand signals from where people are actually complaining</span>. Pick your starting point.
        </p>

        {/* PRIMARY CTAs */}
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="hero-ctas">
          <button type="button" onClick={onPrimaryPath} data-testid="hero-cta-proven"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white shadow-[0_14px_34px_-12px_rgba(139,92,246,0.6)] hover:-translate-y-0.5 transition-transform"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}>
            Explore Proven Winners <ArrowRight size={17} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button type="button" onClick={onSecondaryPath} data-testid="hero-cta-signals"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-[#0E0E0E] bg-white border border-[#E5E8F0] hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-colors">
            Browse Demand Signals <ArrowRight size={17} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>

      {/* PROOF STRIP */}
      <div className="relative max-w-4xl mx-auto mt-11 sm:mt-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[#5A575B] font-mono-data text-[11px] sm:text-[12px] uppercase tracking-[0.18em]" data-testid="hero-proof-strip">
        <span><span className="text-[#0E0E0E] font-semibold">4,600+</span> startups</span>
        <span className="opacity-40">·</span>
        <span><span className="text-[#0E0E0E] font-semibold">216</span> opportunities</span>
        <span className="opacity-40">·</span>
        <span><span className="text-[#0E0E0E] font-semibold">12,273</span> gaps</span>
        <span className="opacity-40">·</span>
        <span><span className="text-[#0E0E0E] font-semibold">$24M/mo</span> real revenue</span>
        <span className="opacity-40">·</span>
        <span className="text-[#8B5CF6]">every number verifiable</span>
      </div>

      {/* Freshness note — bi-weekly refresh */}
      <div className="relative mt-4 flex items-center justify-center gap-2 text-[11px] sm:text-[12px] text-[#5A575B]" data-testid="hero-refresh-note">
        <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span>Fresh data — the corpus <span className="font-semibold text-[#0E0E0E]">auto-refreshes every 2 weeks</span> · last updated 15 Jun 2026</span>
      </div>
    </section>
  );
};

// ============================================================
// LIVE SCANNER MOCKUP — animated faux dashboard card
// ============================================================
const SCANNER_SCENARIOS = [
  {
    avatar: "R", avatarBg: "linear-gradient(135deg, #FF9966, #FF5E62)",
    source: "r/SaaS · 2m ago",
    quote: "I spend 6 hours every week scrolling Reddit looking for SaaS ideas. There has to be a better way.",
    score: 87, verdict: "HOME RUN",
    aiResponse: "Reddit demand strong, no direct competitor, automatable with Claude + scrapers.",
    found: "AI Reddit Idea Scout · $19/mo · 2-day MVP plan ready",
  },
  {
    avatar: "H", avatarBg: "linear-gradient(135deg, #F97316, #EA580C)",
    source: "Hacker News · 4m ago",
    quote: "Tired of writing the same cold email variants over and over. Need a way to A/B test outreach copy without a CRM.",
    score: 92, verdict: "HOME RUN",
    aiResponse: "B2B-ready, 14 Fiverr gigs already exist at $40-$120. Strong willingness-to-pay signal.",
    found: "Cold Email A/B Lab · $29/mo · MVP scope estimated 3 days",
  },
  {
    avatar: "F", avatarBg: "linear-gradient(135deg, #1DBF73, #0E8E5C)",
    source: "Fiverr brief · 6m ago",
    quote: "Need someone to monitor 5 competitor pricing pages daily and Slack me whenever they change. Budget $200/mo.",
    score: 78, verdict: "STRONG",
    aiResponse: "Recurring revenue model, 4 paying Fiverr clients already, simple Playwright scraper. Ship in a weekend.",
    found: "Pricing Sentinel · $49/mo · 2-day MVP plan ready",
  },
];

const useAnimatedCount = (target, deps = []) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let frame; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return val;
};

const LiveScannerMockup = () => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("typing"); // "typing" | "answer"
  const scenario = SCANNER_SCENARIOS[idx];

  // Animated stat values (count up; cycle small deltas each scenario)
  const baseStats = useMemo(() => ({
    signals: 47 + idx * 3,
    top: 8 + idx,
    avg: 74 + idx,
    reddit: 623 + idx * 12,
  }), [idx]);
  const signals = useAnimatedCount(baseStats.signals, [idx]);
  const top = useAnimatedCount(baseStats.top, [idx]);
  const avg = useAnimatedCount(baseStats.avg, [idx]);
  const reddit = useAnimatedCount(baseStats.reddit, [idx]);
  const score = useAnimatedCount(scenario.score, [idx, phase === "answer"]);

  // Cycle: typing 1.6s → answer 4.4s → next scenario
  useEffect(() => {
    setPhase("typing");
    const t1 = setTimeout(() => setPhase("answer"), 1700);
    const t2 = setTimeout(() => setIdx((i) => (i + 1) % SCANNER_SCENARIOS.length), 6500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [idx]);

  const stats = [
    { label: "Signals Today",   value: signals, delta: "+12%", bar: "bg-[#5E73C1]", barW: "78%" },
    { label: "Top Scored",      value: top,     delta: "+25%", bar: "bg-emerald-500", barW: "64%" },
    { label: "Avg P2P Score",   value: avg,     delta: "+6",   bar: "bg-amber-400", barW: "74%" },
    { label: "Reddit Mentions", value: reddit,  delta: "+8%",  bar: "bg-[#C084FC]", barW: "88%" },
  ];

  return (
    <div
      className="relative w-full max-w-full sm:max-w-[440px] rounded-[20px] sm:rounded-[24px] p-3.5 sm:p-5 overflow-hidden lum-mockup-float"
      style={{
        background: "linear-gradient(140deg, #FFFFFF 0%, #FAFBFE 100%)",
        boxShadow: "0 30px 80px -25px rgba(94,115,193,0.40), 0 4px 24px -8px rgba(139,92,246,0.15), 0 0 0 1px rgba(94,115,193,0.10)",
      }}
      data-testid="hero-live-mockup"
    >
      {/* Sweeping scan-line */}
      <div className="lum-scanline" />
      {/* Subtle ambient gradient orb */}
      <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-60 pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(139,92,246,0.18), transparent 70%)", filter: "blur(20px)" }} />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-2 mb-4">
        <div className="flex items-start gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5E73C1] to-[#8B5CF6] flex items-center justify-center shadow-sm lum-grad-shift flex-shrink-0">
            <ChartLineUp size={16} weight="bold" className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] tracking-[0.18em] uppercase text-[#5A575B] font-semibold">Live Scanner</div>
            <div className="text-[13px] text-[#0E0E0E] font-medium truncate">Real-time signal feed</div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-semibold text-white shadow-sm lum-grad-shift flex-shrink-0 whitespace-nowrap"
             style={{ background: "linear-gradient(110deg, #5E73C1 0%, #8B5CF6 100%)" }}>
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-emerald-300 animate-ping opacity-70" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-300" />
          </span>
          <span className="hidden sm:inline">Reddit · HN · Fiverr · Freelancer</span>
          <span className="sm:hidden">4 live sources</span>
        </div>
      </div>

      {/* 2x2 stat grid */}
      <div className="relative grid grid-cols-2 gap-2.5 mb-4">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className="rounded-xl border border-[#EEF1F7] bg-white p-3 shadow-[0_1px_2px_rgba(14,14,14,0.02)] lum-pop-in"
            style={{ animationDelay: `${0.05 + i * 0.08}s` }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] text-[#5A575B] font-medium">{s.label}</div>
              <div className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">{s.delta}</div>
            </div>
            <div className="text-xl font-bold text-[#0E0E0E] tracking-tight tabular-nums">{s.value.toLocaleString()}</div>
            <div className="mt-2 h-1 rounded-full bg-[#F1F3F8] overflow-hidden">
              <div
                key={`bar-${idx}-${i}`}
                className={`h-full ${s.bar} rounded-full lum-bar`}
                style={{ "--lum-bar-w": s.barW }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Active scan section */}
      <div className="relative mb-2.5 flex items-center justify-between">
        <div className="font-mono-data text-[10px] tracking-[0.2em] uppercase text-[#5A575B] font-semibold">
          Active Scan
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </span>
          LIVE
        </div>
      </div>

      <div key={`conv-${idx}`} className="relative space-y-2 min-h-[180px] sm:min-h-[170px]">
        {/* Caller bubble */}
        <div className="flex items-start gap-2 lum-bubble-in">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 shadow-sm"
            style={{ background: scenario.avatarBg }}
          >
            {scenario.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-[#9B9498] font-semibold mb-0.5">{scenario.source}</div>
            <div
              className="bg-[#F4F6FB] rounded-2xl rounded-tl-md px-3 py-2 text-[12px] text-[#0E0E0E] leading-snug break-words"
              style={{ overflowWrap: "anywhere" }}
            >
              "{scenario.quote}"
            </div>
          </div>
        </div>

        {/* AI response or typing */}
        {phase === "typing" ? (
          <div className="flex items-start gap-2 justify-end lum-bubble-in" style={{ animationDelay: "0.4s" }}>
            <div className="flex-1 min-w-0 flex flex-col items-end">
              <div className="text-[9px] uppercase tracking-wider text-[#9B9498] font-semibold mb-0.5">Prime Pillar AI · scoring…</div>
              <div className="rounded-2xl rounded-tr-md px-3 py-2 bg-white border border-[#EEF1F7] shadow-sm inline-flex items-center gap-1">
                <span className="lum-typing-dot w-1.5 h-1.5 rounded-full bg-[#8B5CF6] inline-block" />
                <span className="lum-typing-dot w-1.5 h-1.5 rounded-full bg-[#8B5CF6] inline-block" />
                <span className="lum-typing-dot w-1.5 h-1.5 rounded-full bg-[#8B5CF6] inline-block" />
              </div>
            </div>
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 shadow-sm"
                 style={{ background: "linear-gradient(135deg, #8B5CF6, #5E73C1)" }}>AI</div>
          </div>
        ) : (
          <div className="flex items-start gap-2 justify-end lum-bubble-in">
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-[#9B9498] font-semibold mb-0.5 text-right">Prime Pillar AI · just now</div>
              <div className="rounded-2xl rounded-tr-md px-3 py-2 text-[12px] text-white leading-snug shadow-sm lum-grad-shift break-words"
                   style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)", overflowWrap: "anywhere" }}>
                <span className="font-bold tabular-nums">Scored {score}/100 — {scenario.verdict}.</span>{" "}
                {scenario.aiResponse}
              </div>
            </div>
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 shadow-sm"
                 style={{ background: "linear-gradient(135deg, #8B5CF6, #5E73C1)" }}>AI</div>
          </div>
        )}
      </div>

      {/* Bottom — opportunity found card. ALWAYS RENDERED to reserve the
          space so the whole hero never reflows; fades in/out across cycles. */}
      <div
        key={`found-${idx}`}
        className={`relative mt-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-2.5 flex items-center gap-2.5 min-w-0 transition-opacity duration-500 ${phase === "answer" ? "opacity-100 lum-found-pulse" : "opacity-0 pointer-events-none"}`}
        aria-hidden={phase !== "answer"}
        data-testid="hero-mockup-opportunity"
      >
        <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
          <Trophy size={15} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[#0E0E0E]">Opportunity Found</div>
          <div className="text-[11px] text-[#3F3D40] mt-0.5 truncate">{scenario.found}</div>
        </div>
        <button
          tabIndex={phase === "answer" ? 0 : -1}
          className="px-3 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold transition-colors shadow-sm flex-shrink-0"
        >
          View
        </button>
      </div>
    </div>
  );
};

// 3D-feel crystal prism — pure CSS, animated (legacy, kept for compat)
const CrystalPrism = () => (
  <div className="relative w-[280px] h-[280px]" aria-hidden="true">
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#8B5CF6]/30 to-[#5E73C1]/30" />
  </div>
);

// Scanner live pill
const ScannerLivePill = ({ scannerLive }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  if (!scannerLive) return null;
  const { running, currentTask, nextRunTs } = scannerLive;
  let label, dotClass;
  if (running) {
    label = <><span className="text-[#0E0E0E]">Scanning live</span><span className="text-[#5A575B]"> · {currentTask || "fetching signals…"}</span></>;
    dotClass = "bg-emerald-400";
  } else if (nextRunTs) {
    const diffMs = nextRunTs - now;
    if (diffMs <= 0) {
      label = <span className="text-[#0E0E0E]">Scan starting any moment</span>;
      dotClass = "bg-amber-400";
    } else {
      const totalMin = Math.max(1, Math.round(diffMs / 60000));
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      const human = h > 0 ? `${h}h ${m}m` : `${m}m`;
      label = <><span className="text-[#0E0E0E]">Next scan in {human}</span><span className="text-[#5A575B]"> · 3× daily (06 / 12 / 18 UTC)</span></>;
      dotClass = "bg-[#8B5CF6]";
    }
  } else return null;
  return (
    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full lum-glass text-xs sm:text-sm font-mono-data" data-testid="hero-scanner-pill" data-scanner-running={running ? "1" : "0"}>
      <span className="relative flex w-2 h-2">
        <span className={`absolute inset-0 rounded-full ${dotClass} opacity-60 animate-ping`} />
        <span className={`relative w-2 h-2 rounded-full ${dotClass}`} />
      </span>
      <span>{label}</span>
    </div>
  );
};

// ============================================================
// VS COMPETITION — head-to-head comparison matrix
// ============================================================
const COMPARISON_ROWS = [
  {
    feature: "Where ideas come from",
    pp: "4,613 funded startups + 7 live demand platforms",
    ib: "Editorial + AI-generated",
    bdb: "Curated static database",
  },
  {
    feature: "Grounded, no hallucination",
    pp: "Every claim cites a real source or a visible ⚐ honest deferral",
    ib: false,
    bdb: false,
  },
  {
    feature: "Freshness",
    pp: "4× daily auto-scans · 02/06/12/18 UTC",
    ib: "1 idea per day",
    bdb: "Infrequent updates",
  },
  {
    feature: "Per-idea evidence",
    pp: "Every opp links to the real Reddit/Fiverr/Upwork post",
    ib: "Editorial write-up only",
    bdb: "Description only",
  },
  {
    feature: "Demand scoring",
    pp: "Deterministic 0-100 P2P Score · fully auditable",
    ib: "Qualitative tags",
    bdb: "Categories only",
  },
  {
    feature: "Cross-source validation",
    pp: "✓✓✓ emerald badge when 3+ platforms agree",
    ib: false,
    bdb: false,
  },
  {
    feature: "Real revenue dataset",
    pp: "2,300+ live-earning bootstrapped products (from 7,600+ tracked) with self-reported MRR ($24M/mo total) — clone what already makes money",
    ib: false,
    bdb: false,
  },
  {
    feature: "Competition reality-check",
    pp: "GREEN / YELLOW / RED verdict from live GitHub, Hacker News & web competitor search — overrides the hype",
    ib: false,
    bdb: false,
  },
  {
    feature: "Proven winners to clone",
    pp: "216 funded models mapped to the exact market they're still missing from",
    ib: false,
    bdb: false,
  },
  {
    feature: "Per-company teardowns",
    pp: "Evaluate any funded startup: where to launch, what customers hate, missing features, pricing gaps",
    ib: false,
    bdb: false,
  },
  {
    feature: "\u201CShould I build this?\u201D verdict",
    pp: "Instant Build / Proceed-with-caution call per company \u2014 with the reasons spelled out",
    ib: false,
    bdb: false,
  },
  {
    feature: "Best country to launch",
    pp: "Every opportunity matched to the market it fits best, with an agree/disagree confidence call",
    ib: false,
    bdb: false,
  },
  {
    feature: "Real search-volume trends",
    pp: "Live Google search volume + growth across 12,547 topics in 11 verticals — with a 🔥 Exploding flag",
    ib: "Trend mentions only",
    bdb: false,
  },
  {
    feature: "Demand × competition verdict",
    pp: "💎 Emerging / Competitive / 🔴 Saturated on every market — demand fused with 6,400 real funded startups",
    ib: false,
    bdb: false,
  },
  {
    feature: "Live competition reality-check",
    pp: "Every market is checked against the live Google results page — we refuse to fake an 'open lane' badge when products already rank. Genuine whitespace is rare, and we prove it with evidence instead of inventing it",
    ib: false,
    bdb: false,
  },
  {
    feature: "Who's building it (live supply map)",
    pp: "Every topic linked to the actual funded companies building it — funding, headcount & logos",
    ib: false,
    bdb: false,
  },
  {
    feature: "AI research advisor",
    pp: "Grounded co-pilot answers “should I build this?” per topic — cites the real players, gives a build / skip call",
    ib: "Static write-up",
    bdb: false,
  },
  {
    feature: "Momentum tracking",
    pp: "🚀 RISING badges + 7-day trajectory snapshot",
    ib: false,
    bdb: false,
  },
  {
    feature: "AI MVP brief generator",
    pp: "One-click Claude brief · positioning · 3 features · GTM · PDF/CSV export",
    ib: "Some editorial GTM tips",
    bdb: false,
  },
  {
    feature: "Personalized feed",
    pp: "Star 17 niches → starred float to top automatically",
    ib: false,
    bdb: "Category filters only",
  },
  {
    feature: "Visual analytics",
    pp: "Quick Win Quadrant · G2 Pain Index · score distribution",
    ib: "Idea cards",
    bdb: "Spreadsheet view",
  },
];

const Cell = ({ value, accent }) => {
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] text-[#9B9498]">
        <X size={14} weight="bold" className="text-[#C8C2C5] flex-shrink-0" />
        <span className="italic">Not available</span>
      </span>
    );
  }
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] text-emerald-700 font-medium">
        <Check size={14} weight="bold" className="text-emerald-500 flex-shrink-0" />
        Yes
      </span>
    );
  }
  return (
    <span className={`inline-flex items-start gap-1.5 text-[12px] sm:text-[13px] leading-snug ${accent ? "text-[#0E0E0E] font-medium" : "text-[#3F3D40]"}`}>
      {accent && <Check size={14} weight="bold" className="text-[#8B5CF6] flex-shrink-0 mt-0.5" />}
      <span>{value}</span>
    </span>
  );
};

const VsCompetitionSection = ({ onCta }) => {
  return (
    <section id="vs-competition" className="relative px-5 sm:px-8 py-16 sm:py-24 overflow-hidden" data-testid="vs-competition-section">
      {/* Background washes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-0 w-[400px] h-[400px] rounded-full bg-violet-500/8 blur-[140px]" />
        <div className="absolute bottom-10 -left-20 w-[380px] h-[380px] rounded-full bg-blue-500/8 blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#E5E8F0] shadow-sm mb-5">
            <Target size={14} weight="duotone" className="text-[#8B5CF6]" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-[#5A575B]">Head to head</span>
          </div>
          <h2 className="font-display tracking-[-0.03em] text-3xl sm:text-5xl lg:text-6xl font-bold text-[#0E0E0E] leading-[1.05]">
            Why founders pick Prime Pillar over{" "}
            <span className="italic font-serif-italic text-[#8B5CF6]">"idea databases"</span>.
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg text-[#5A575B] max-w-2xl mx-auto leading-relaxed px-2">
            Idea databases hand you a paragraph. We hand you the live <b>demand</b> (real Google search volume) <i>and</i> the <b>supply</b> (the funded startups already building it) side-by-side — so every market comes with a 💎 Emerging / Competitive / 🔴 Saturated verdict, each one checked against live Google competition, the actual players, and an AI advisor to pressure-test it. Every claim cited or honestly flagged.
          </p>
        </div>

        {/* ===== Desktop matrix (md+) ===== */}
        <div className="hidden md:block relative">
          <div className="rounded-3xl border border-[#E5E8F0] bg-white/80 backdrop-blur-sm shadow-[0_30px_80px_-40px_rgba(94,115,193,0.30)] overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-[1.2fr_1.4fr_1fr_1fr] border-b border-[#EEF1F7]">
              <div className="p-5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#9B9498]">Feature</div>
              <div className="relative p-5 pt-7 bg-gradient-to-br from-violet-50 via-white to-blue-50 border-x border-violet-200">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-[9px] font-bold tracking-[0.15em] uppercase shadow-md whitespace-nowrap">
                  Recommended
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                    <span className="text-white font-display font-bold text-sm">P</span>
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold text-[#0E0E0E] leading-tight">Prime Pillar</div>
                    <div className="text-[11px] text-violet-700 font-semibold">from $19/mo</div>
                  </div>
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-base font-bold text-[#0E0E0E] leading-tight">Idea Browser</div>
                <div className="text-[11px] text-[#9B9498] mt-0.5">$40-50/mo</div>
              </div>
              <div className="p-5">
                <div className="font-display text-base font-bold text-[#0E0E0E] leading-tight">BigIdeasDB</div>
                <div className="text-[11px] text-[#9B9498] mt-0.5">$99 one-time</div>
              </div>
            </div>

            {/* Feature rows */}
            {COMPARISON_ROWS.map((row, i) => (
              <div
                key={row.feature}
                className={`grid grid-cols-[1.2fr_1.4fr_1fr_1fr] ${i !== COMPARISON_ROWS.length - 1 ? "border-b border-[#F4F6FB]" : ""} hover:bg-violet-50/30 transition-colors`}
              >
                <div className="p-4 sm:p-5 text-[13px] font-semibold text-[#0E0E0E] flex items-center">
                  {row.feature}
                </div>
                <div className="p-4 sm:p-5 bg-violet-50/40 border-x border-violet-100 flex items-center">
                  <Cell value={row.pp} accent />
                </div>
                <div className="p-4 sm:p-5 flex items-center">
                  <Cell value={row.ib} />
                </div>
                <div className="p-4 sm:p-5 flex items-center">
                  <Cell value={row.bdb} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== Mobile stacked cards (below md) ===== */}
        <div className="md:hidden space-y-4">
          {/* Prime Pillar card — highlighted */}
          <div className="relative rounded-2xl border-2 border-violet-300 bg-gradient-to-br from-violet-50/80 via-white to-blue-50/60 p-5 pt-8 shadow-[0_20px_50px_-25px_rgba(139,92,246,0.4)] overflow-hidden">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-[9px] font-bold tracking-[0.15em] uppercase shadow-md whitespace-nowrap">
              Recommended
            </div>
            <div className="flex items-center gap-2 mb-4 mt-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-display font-bold text-base">P</span>
              </div>
              <div>
                <div className="font-display text-lg font-bold text-[#0E0E0E]">Prime Pillar</div>
                <div className="text-[11px] text-violet-700 font-semibold">from $19/mo</div>
              </div>
            </div>
            <ul className="space-y-2.5">
              {COMPARISON_ROWS.map((row) => (
                <li key={row.feature} className="flex items-start gap-2.5 text-[13px]">
                  <Check size={14} weight="bold" className="text-[#8B5CF6] flex-shrink-0 mt-1" />
                  <span className="text-[#3F3D40] leading-snug">
                    <span className="font-semibold text-[#0E0E0E]">{row.feature}:</span>{" "}
                    {typeof row.pp === "string" ? row.pp : "Yes"}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Competitors — collapsed list */}
          {[
            { name: "Idea Browser", price: "$40-50/mo", rows: COMPARISON_ROWS.map(r => ({ f: r.feature, v: r.ib })) },
            { name: "BigIdeasDB", price: "$99 one-time", rows: COMPARISON_ROWS.map(r => ({ f: r.feature, v: r.bdb })) },
          ].map((comp) => (
            <div key={comp.name} className="rounded-2xl border border-[#E5E8F0] bg-white/80 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base font-bold text-[#0E0E0E]">{comp.name}</div>
                <div className="text-[11px] text-[#9B9498] font-semibold">{comp.price}</div>
              </div>
              <ul className="space-y-2">
                {comp.rows.map(({ f, v }) => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px] leading-snug">
                    {v === false ? (
                      <>
                        <X size={13} weight="bold" className="text-[#C8C2C5] flex-shrink-0 mt-0.5" />
                        <span className="text-[#9B9498]"><span className="font-medium text-[#5A575B]">{f}:</span> <span className="italic">Not available</span></span>
                      </>
                    ) : (
                      <>
                        <div className="w-3.5 h-3.5 rounded-full bg-[#F4F6FB] flex-shrink-0 mt-0.5" />
                        <span className="text-[#5A575B]"><span className="font-medium text-[#3F3D40]">{f}:</span> {v}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onCta}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full text-base font-semibold text-white shadow-[0_18px_40px_-12px_rgba(139,92,246,0.55)] hover:shadow-[0_20px_50px_-10px_rgba(139,92,246,0.7)] hover:-translate-y-0.5 transition-all"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
            data-testid="vs-competition-cta"
          >
            Start with Prime Pillar — Free
            <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <p className="text-[11px] sm:text-xs text-[#9B9498] font-medium tracking-wide text-center">
            No credit card · 1 free deep-dive included
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// FEATURE GRID — bento style
// ============================================================
const FeaturesSection = () => {
  const items = [
    { icon: Cube, title: "4,613-company VC corpus", body: "YC, a16z, Accel and Sequoia portfolios — deduped, enriched, and refreshed bi-weekly into one analyzable dataset.", tone: "royal" },
    { icon: Trophy, title: "2,300+ bootstrapped winners", body: "Indie products already earning real MRR ($24M/mo total, from 7,600+ tracked via TrustMRR) — clone what already makes money.", tone: "rust" },
    { icon: Crosshair, title: "Gap detection across 65 markets", body: "We map which proven model is under-served in which country — scored gaps collapsed into ranked, source-cited opportunities.", tone: "royal" },
    { icon: MagnifyingGlass, title: "7 live demand platforms", body: "Reddit, Fiverr, Upwork, G2, ProductHunt, YouTube and Google Trends — scraped daily and clustered into scored pain.", tone: "rust" },
    { icon: ChartLineUp, title: "Trending demand board", body: "12,547 topics across 11 verticals, ranked by real Google search volume + 12-month growth, each with a 🔥 Exploding flag.", tone: "royal" },
    { icon: Target, title: "Demand × competition verdict", body: "Every topic stamped 💎 Emerging / Competitive / 🔴 Saturated by fusing demand with 6,400 funded startups — then verified against the live Google results page, so verdicts reflect real competition, not guesses. 2,330 Emerging markets have proven demand and room to enter.", tone: "rust" },
    { icon: Sparkle, title: "AI research co-pilot", body: "Ask “should I build this?” on any topic — grounded answers cite the real funded players and give a build / skip call. SaaS or physical.", tone: "royal" },
    { icon: Pulse, title: "Deterministic 0–100 P2P score", body: "A transparent weighted blend of demand, competition, automation, revenue ceiling and speed-to-MVP — never a black box.", tone: "rust" },
    { icon: ShieldCheck, title: "Web-search grounding", body: "Claude grounds every claim in a real source — or emits a visible ⚐ honest deferral. Fabrication never reaches you.", tone: "royal" },
  ];
  return (
    <section id="features" className="relative px-5 sm:px-8 py-14 sm:py-20" data-testid="features-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <Eyebrow tone="royal">Everything inside</Eyebrow>
          <GradHeading className="mt-5 text-4xl sm:text-6xl lg:text-7xl">
            One subscription, <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">both engines</span>.
          </GradHeading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {items.map((it, i) => {
            const Icon = it.icon;
            const isRust = it.tone === "rust";
            return (
              <div key={it.title} className="relative lum-glass rounded-2xl p-7 group hover:bg-white/8 transition-all overflow-hidden" data-testid={`feature-${i}`}>
                <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-50 ${isRust ? "bg-[#8B5CF6]/30" : "bg-[#5E73C1]/30"} group-hover:opacity-80 transition-opacity`} />
                <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center ${isRust ? "bg-[#8B5CF6]/15 text-[#6D45D6]" : "bg-[#5E73C1]/20 text-[#3F4E8A]"} border ${isRust ? "border-[#8B5CF6]/40" : "border-[#5E73C1]/45"}`}>
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="relative mt-5 font-display text-xl text-[#0E0E0E] tracking-tight font-semibold">{it.title}</h3>
                <p className="relative mt-2 text-sm text-[#3F3D40] leading-relaxed">{it.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// NUMBERS
// ============================================================
const ByTheNumbersSection = () => {
  const groups = [
    {
      label: "Proven Winners engine",
      accent: "#8B5CF6",
      stats: [
        { num: "4,613", label: "VC-backed startups analyzed", sub: "YC · a16z · Accel · Sequoia" },
        { num: "216", label: "ranked, grounded opportunities", sub: "from 12,273 detected gaps" },
        { num: "804", label: "acquired exits inside the corpus", sub: "proof the model can win" },
      ],
    },
    {
      label: "Demand Signals engine",
      accent: "#5E73C1",
      stats: [
        { num: "531", label: "scored demand signals", sub: "7 live platforms, swept daily" },
        { num: "387", label: "live AI opportunities", sub: "from 6,190 AI signals" },
        { num: "65", label: "markets covered", sub: "every gap mapped to a country" },
      ],
    },
    {
      label: "Trending demand board",
      accent: "#10b981",
      stats: [
        { num: "12,547", label: "trending topics tracked", sub: "real Google search volume · 11 verticals" },
        { num: "6,404", label: "funded startups in the supply map", sub: "who's building each topic" },
        { num: "2,330", label: "💎 Emerging markets", sub: "proven demand · few players · room to enter" },
      ],
    },
  ];
  return (
    <section className="relative px-5 sm:px-8 py-14 sm:py-20" data-testid="by-the-numbers">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-12">
          <Eyebrow tone="rust">By the numbers</Eyebrow>
          <GradHeading className="mt-5 text-3xl sm:text-5xl lg:text-6xl">
            Real data. <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">Not vibes.</span>
          </GradHeading>
          <p className="mt-4 text-sm sm:text-base text-[#5A575B]">Snapshot · 2026-06-22 · every figure is visible and verifiable inside the app.</p>
        </div>
        <div className="space-y-10">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="flex items-center gap-2.5 mb-4 px-1">
                <span className="w-2 h-2 rounded-full" style={{ background: g.accent }} />
                <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#5A575B] font-semibold">{g.label}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                {g.stats.map((s, i) => (
                  <div key={s.label} className="relative lum-glass rounded-2xl p-5 sm:p-8 group hover:bg-white/8 transition-all overflow-hidden" data-testid={`number-stat-${g.accent}-${i}`}>
                    <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl transition-colors" style={{ background: `${g.accent}22` }} />
                    <div className="relative font-display font-bold text-[#0E0E0E] text-5xl sm:text-7xl leading-none tracking-tight tabular-nums">{s.num}</div>
                    <div className="relative mt-5 text-base font-medium text-[#0E0E0E]">{s.label}</div>
                    <div className="relative mt-1 text-sm text-[#5A575B]">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// PRICING
// ============================================================
const PricingTeaser = ({ onPricing }) => {
  const tiers = [
    { name: "Scout", price: "Free", features: ["Top 5 signals weekly", "Basic score breakdown", "1 Deep-Dive / month"] },
    { name: "Founder", price: "$19", per: "/mo", featured: true, features: ["Full signal feed (50+)", "Unlimited Deep-Dives", "Validate-an-Idea AI scorer", "Watchlist · PDF exports"] },
    { name: "Pro", price: "$49", per: "/mo", features: ["Everything in Founder", "Trending Pain Board access", "Priority scan refresh", "Email digest (Resend)"] },
  ];
  return (
    <section id="pricing" className="relative px-5 sm:px-8 py-14 sm:py-20" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <Eyebrow tone="rust">The Price</Eyebrow>
          <GradHeading className="mt-5 text-4xl sm:text-6xl lg:text-7xl">
            One coffee a month. <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">Forever profitable.</span>
          </GradHeading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
          {tiers.map((t) => (
            <div key={t.name} className={`relative rounded-2xl p-7 sm:p-9 ${t.featured ? "lum-glass border-[#8B5CF6]/40 lum-glow-rust" : "lum-glass"}`} data-testid={`pricing-tier-${t.name.toLowerCase()}`}>
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#8B5CF6] text-white text-[10px] font-mono-data uppercase tracking-[0.25em]">Most popular</div>
              )}
              <div className="font-mono-data text-[11px] uppercase tracking-[0.25em] text-[#5A575B]">{t.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-bold text-5xl sm:text-6xl leading-none text-[#0E0E0E]">{t.price}</span>
                {t.per && <span className="text-sm text-[#5A575B]">{t.per}</span>}
              </div>
              <ul className="mt-7 space-y-2.5 text-sm text-[#0E0E0E]">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <span className="text-[#8B5CF6]">✦</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={onPricing} className={`mt-7 w-full px-5 py-3 rounded-full text-sm font-semibold transition-all ${t.featured ? "bg-[#8B5CF6] text-white hover:bg-[#7C4DEC]" : "bg-white/8 text-[#0E0E0E] border border-white/15 hover:bg-white/15"}`}>
                Get started
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// FAQ
// ============================================================
const FAQSection = () => {
  const faqs = [
    { q: "What's the difference between Discover and Signals?", a: "Two engines, one platform. Discover (Proven Winners) works top-down: it reverse-engineers 4,613 funded startups and shows you which proven model is missing from which market, with a grounded brief. Signals (Demand Signals) works bottom-up: it scans 7 live platforms for raw, scored pain and drafts an MVP brief. Use whichever matches how you think." },
    { q: "How do I know the numbers aren't made up?", a: "Because the whole system is built to refuse fabrication. Every hard claim — a competitor, a feature, a pain quote, a market figure — cites a real source (a G2 review, a web-search URL, a cluster fact). When evidence isn't there, you see a visible ⚐ \u201cno grounded source — verify before acting\u201d deferral, never a confident guess. Two independent fact-check rounds removed every fabrication before launch." },
    { q: "What sources do you scan?", a: "Seven premium platforms via Apify (Reddit, Fiverr, Upwork, G2 Reviews, ProductHunt, YouTube, Google Trends) plus a legacy RSS layer (Reddit RSS, Hacker News Ask HN, Freelancer.com). The Proven Winners engine separately ingests 4,613 funded startups from YC, a16z, Accel and Sequoia." },
    { q: "How is the P2P Score calculated?", a: "Weighted blend: demand (30%), competition (25%), automation potential (20%), revenue ceiling (15%), speed-to-MVP (10%). Validated against real Fiverr/Freelancer budgets and boosted by Google Trends momentum." },
    { q: "What's the Cross-Source Validator?", a: "Same pain on Reddit + Fiverr + Upwork = near-impossible noise; that's organic demand. Triple-sourced opportunities get ✓✓✓ emerald badges. Toggle 'Validated only' to filter the feed down to the highest-confidence picks." },
    { q: "What's the Quick Win Quadrant?", a: "A live 2×2 SVG matrix plotting every opportunity on P2P score × distinct source count. The top-right emerald cell is your must-build shortlist. Opportunities trending upward get a pulsing rose 🚀 RISING badge." },
    { q: "Can I validate my own idea?", a: "Yes. Paste any idea into the Validate-an-Idea panel. We score it against our live 11,400+ raw signal dataset and save it to your Signals feed as a 'My Idea'." },
    { q: "What's the G2 Pain Index + Build Alternative?", a: "We rank every B2B SaaS on G2 by negative-review density. One click → Claude drafts an MVP brief with positioning, target persona, 3 features (with verbatim complaint quotes as evidence), suggested pricing, GTM angle, and an ad headline. Save to the library and export to PDF or CSV." },
    { q: "What's in a Deep-Dive report?", a: "Competitor sweep, market size estimate, 5-day MVP plan, recommended stack, ready landing copy, validation tweets." },
    { q: "Do you sell my data?", a: "No. Signals are scraped from public sources and shared with all subscribers. Your watchlist, niches, and brief library are private." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="relative px-5 sm:px-8 py-14 sm:py-20" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 text-center">
          <Eyebrow tone="mute">Questions</Eyebrow>
          <GradHeading className="mt-5 text-4xl sm:text-6xl">Things people ask.</GradHeading>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="lum-glass rounded-2xl overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left text-[#0E0E0E] hover:bg-white/5 transition-colors">
                <span className="font-display text-base sm:text-lg">{f.q}</span>
                <span className="flex-shrink-0 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-[#8B5CF6] transition-transform" style={{ transform: open === i ? "rotate(45deg)" : "none" }}>
                  <Plus size={14} weight="bold" />
                </span>
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-[#5A575B] leading-relaxed text-sm sm:text-base border-t border-white/8 pt-4">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// FINAL CTA + FOOTER
// ============================================================
const FinalCTASection = ({ onPrimaryPath, onSecondaryPath }) => (
  <section className="relative px-5 sm:px-8 py-16 sm:py-24 text-center overflow-hidden" data-testid="final-cta">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#8B5CF6]/20 blur-[180px]" />
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full bg-[#5E73C1]/25 blur-[120px]" />
    </div>
    <div className="relative max-w-4xl mx-auto">
      <Eyebrow tone="rust">The end of guessing</Eyebrow>
      <GradHeading className="mt-6 text-4xl sm:text-6xl lg:text-7xl">
        Stop guessing.<br />
        <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">Start from evidence.</span>
      </GradHeading>
      <p className="mt-8 text-[#5A575B] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
        Clone a proven, funded playbook into a market that's missing it — or catch raw demand the moment it's voiced. Pick your starting point.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <button
          onClick={onPrimaryPath}
          data-testid="final-cta-proven"
          className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full text-base font-semibold text-white shadow-[0_18px_40px_-12px_rgba(139,92,246,0.55)] hover:-translate-y-0.5 transition-all"
          style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
        >
          <Trophy size={16} weight="fill" /> Explore Proven Winners
          <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </button>
        <button
          onClick={onSecondaryPath}
          data-testid="final-cta-signals"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-full text-base font-semibold text-[#0E0E0E] bg-white border border-[#E5E8F0] hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-all shadow-sm"
        >
          <Pulse size={15} weight="bold" /> Open Demand Signals
        </button>
      </div>
    </div>
  </section>
);

const BigFooterSection = () => (
  <footer className="relative border-t border-white/8 px-5 sm:px-8 pt-16 pb-8 bg-[#F1F3F8]" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-10">
        {/* Brand + tagline + company of record */}
        <div className="col-span-2 md:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <LuminaLogo size={56} testid="footer-logo" />
            <PrimePillarWordmark height={38} testid="footer-wordmark" />
          </div>
          <div className="font-serif-italic text-[#5A575B] text-xl sm:text-2xl max-w-sm leading-tight mb-6">
            "Clone what's proven. Catch what's painful. Never guess."
          </div>
          <address className="not-italic text-[13px] leading-relaxed text-[#5A575B]" data-testid="footer-company">
            <div className="font-semibold text-[#0E0E0E]">Lulo Software LLC</div>
            <div>30 N Gould St, Ste R</div>
            <div>Sheridan, WY 82801, USA</div>
            <a href="mailto:support@lulosoftware.com" className="text-violet-700 hover:underline" data-testid="footer-email">support@lulosoftware.com</a>
          </address>
        </div>

        {/* Product */}
        <div className="md:col-span-3 md:col-start-7">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-[#9B9498] mb-4">Product</div>
          <ul className="space-y-2.5 text-[14px] text-[#444048]">
            <li><a href="#two-products-section" className="hover:text-violet-700 transition-colors" data-testid="footer-link-discover">Proven Winners</a></li>
            <li><a href="#trending" className="hover:text-violet-700 transition-colors" data-testid="footer-link-signals">Demand Signals</a></li>
            <li><a href="#features" className="hover:text-violet-700 transition-colors" data-testid="footer-link-features">Features</a></li>
            <li><a href="#pricing" className="hover:text-violet-700 transition-colors" data-testid="footer-link-pricing">Pricing</a></li>
          </ul>
        </div>

        {/* Company / Legal */}
        <div className="md:col-span-3">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-[#9B9498] mb-4">Company</div>
          <ul className="space-y-2.5 text-[14px] text-[#444048]">
            <li><a href="#how-it-works" className="hover:text-violet-700 transition-colors" data-testid="footer-link-how">How it works</a></li>
            <li><a href="/terms" className="hover:text-violet-700 transition-colors" data-testid="footer-link-terms">Terms of Service</a></li>
            <li><a href="/privacy" className="hover:text-violet-700 transition-colors" data-testid="footer-link-privacy">Privacy Policy</a></li>
            <li><a href="mailto:support@lulosoftware.com" className="hover:text-violet-700 transition-colors" data-testid="footer-link-contact">Contact</a></li>
          </ul>
        </div>
      </div>

      <div className="mt-14 h-px bg-zinc-200" />
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] font-mono-data uppercase tracking-[0.22em] text-[#9B9498]">
        <div data-testid="footer-copyright">© 2026 Prime Pillar — a product of Lulo Software LLC · All signals scraped, none sold</div>
        <div className="flex items-center gap-5">
          <span>Est. 2026 · Vol. I</span>
          <a href="/terms" className="hover:text-[#0E0E0E] transition-colors">Terms</a>
          <a href="/privacy" className="hover:text-[#0E0E0E] transition-colors">Privacy</a>
        </div>
      </div>
    </div>
  </footer>
);

// ============================================================
// TWO PRODUCTS — the core "what Prime Pillar is now" explainer
// ============================================================
const PipelineStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#8B5CF6]/12 text-[#6D45D6] text-[10px] font-mono-data font-bold flex items-center justify-center mt-0.5">{n}</span>
    <span className="text-[13.5px] sm:text-sm text-[#3F3D40] leading-relaxed">{children}</span>
  </li>
);

const TwoProductsSection = ({ onPrimaryPath, onSecondaryPath }) => (
  <section id="how-it-works" className="relative px-4 sm:px-8 py-10 sm:py-24 overflow-hidden" data-testid="two-products-section">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-10 -left-32 w-[460px] h-[460px] rounded-full bg-violet-500/10 blur-[130px]" />
      <div className="absolute bottom-10 -right-32 w-[460px] h-[460px] rounded-full bg-[#5E73C1]/10 blur-[130px]" />
    </div>
    <div className="relative max-w-6xl mx-auto">
      <div className="text-center mb-12 sm:mb-16">
        <Eyebrow tone="royal">One platform · two engines</Eyebrow>
        <GradHeading className="mt-5 text-3xl sm:text-5xl lg:text-6xl">
          Two ways in. <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">Both grounded in evidence.</span>
        </GradHeading>
        <p className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg text-[#5A575B] max-w-2xl mx-auto leading-relaxed">
          Start top-down from a proven, funded playbook — or bottom-up from raw, unfiltered demand. Same trust rules either way: nothing is fabricated.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 items-stretch">
        {/* ENGINE 1 — Proven Winners */}
        <div className="relative flex flex-col rounded-3xl bg-white p-7 sm:p-9 ring-2 ring-[#8B5CF6] shadow-[0_30px_70px_-28px_rgba(139,92,246,0.5)]" data-testid="two-products-proven">
          <span className="absolute top-6 right-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] text-[10px] font-mono-data uppercase tracking-[0.15em]">
            <Star size={11} weight="fill" /> Most defensible
          </span>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md" style={{ background: "linear-gradient(135deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}>
            <Trophy size={22} weight="fill" />
          </div>
          <h3 className="mt-5 text-2xl font-bold text-[#0E0E0E]">Proven Winners to Clone</h3>
          <p className="mt-1.5 text-sm text-[#5A575B]">Reverse-engineer the funded playbook — top-down.</p>
          <ul className="mt-6 space-y-3">
            <PipelineStep n="1">Ingest <b className="text-[#0E0E0E]">4,613 VC-backed startups</b> (YC · a16z · Accel · Sequoia) <b className="text-[#0E0E0E]">+ 2,300+ bootstrapped winners</b> earning real MRR (from 7,600+ tracked via TrustMRR).</PipelineStep>
            <PipelineStep n="2">Embed &amp; cluster by business model into <b className="text-[#0E0E0E]">1,657 clusters</b>.</PipelineStep>
            <PipelineStep n="3">Detect which proven model is missing from which of <b className="text-[#0E0E0E]">65 markets</b> — <b className="text-[#0E0E0E]">12,273 gaps</b> scored.</PipelineStep>
            <PipelineStep n="4">Ground every claim in a real source → <b className="text-[#0E0E0E]">483 briefs</b>, collapsed to <b className="text-[#0E0E0E]">216 ranked opportunities</b>.</PipelineStep>
          </ul>
          <div className="mt-6 rounded-xl bg-[#F1F3F8] border border-[#E5E8F0] px-4 py-3.5 text-[13px] text-[#0E0E0E]">
            <span className="font-mono-data text-[10px] uppercase tracking-[0.15em] text-[#5A575B]">You get</span>
            <div className="mt-1 font-semibold leading-relaxed">216 ranked opportunities · 2,300+ bootstrapped winners ($24M/mo proven revenue) · per-company teardowns on 4,185 companies · 804 acquired exits as proof</div>
          </div>
          <button
            type="button"
            onClick={onPrimaryPath}
            data-testid="two-products-proven-cta"
            className="group mt-6 inline-flex items-center gap-2 self-start px-6 py-3.5 rounded-full text-sm font-semibold text-white shadow-[0_14px_34px_-12px_rgba(139,92,246,0.6)] hover:-translate-y-0.5 transition-transform"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
          >
            Explore Discover <ArrowRight size={17} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* ENGINE 2 — Raw Demand Signals */}
        <div className="relative flex flex-col rounded-3xl bg-white p-7 sm:p-9 border border-[#E5E8F0] shadow-[0_20px_50px_-26px_rgba(14,14,14,0.28)]" data-testid="two-products-signals">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#F1F3F8] border border-[#E5E8F0] text-[#5E73C1]">
            <Pulse size={22} weight="bold" />
          </div>
          <h3 className="mt-5 text-2xl font-bold text-[#0E0E0E]">Raw Demand Signals</h3>
          <p className="mt-1.5 text-sm text-[#5A575B]">Catch pain the moment it's voiced — bottom-up.</p>
          <ul className="mt-6 space-y-3">
            <PipelineStep n="1">Scan <b className="text-[#0E0E0E]">7 platforms</b> — Reddit · Fiverr · Upwork · G2 · ProductHunt · YouTube · Google Trends.</PipelineStep>
            <PipelineStep n="2">Claude clusters every pain signal and <b className="text-[#0E0E0E]">cross-source validates</b> it (✓✓✓ when 3+ platforms agree).</PipelineStep>
            <PipelineStep n="3">Score it with a <b className="text-[#0E0E0E]">deterministic 0–100 P2P score</b> you can audit — never a black box.</PipelineStep>
            <PipelineStep n="4">One click → a launch-ready <b className="text-[#0E0E0E]">MVP brief</b> (PDF/CSV).</PipelineStep>
          </ul>
          <div className="mt-6 rounded-xl bg-[#F1F3F8] border border-[#E5E8F0] px-4 py-3.5 text-[13px] text-[#0E0E0E]">
            <span className="font-mono-data text-[10px] uppercase tracking-[0.15em] text-[#5A575B]">You get</span>
            <div className="mt-1 font-semibold leading-relaxed">531 scored signals · 387 live AI opportunities · trend tracking</div>
          </div>
          <button
            type="button"
            onClick={onSecondaryPath}
            data-testid="two-products-signals-cta"
            className="group mt-6 inline-flex items-center gap-2 self-start px-6 py-3.5 rounded-full text-sm font-semibold text-[#0E0E0E] bg-white border border-[#E5E8F0] hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-colors"
          >
            Open Signals <ArrowRight size={17} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-center gap-2 text-[11px] sm:text-[12px] text-[#5A575B]" data-testid="two-products-refresh-note">
        <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span>Both engines stay current — <span className="font-semibold text-[#0E0E0E]">Signals refresh daily</span> and the <span className="font-semibold text-[#0E0E0E]">Proven Winners corpus auto-refreshes every 2 weeks</span>, re-grounding only what changed. No fabrication ever enters on refresh.</span>
      </div>

    </div>
  </section>
);

// ============================================================
// TRUST MOAT — the grounding differentiator
// ============================================================
const TrustMoatSection = () => {
  const points = [
    { icon: ShieldCheck, title: "No hallucinated facts", body: "Competitors, features, pain quotes, market figures — none are invented. If an AI can't back a claim with a real source, it doesn't ship." },
    { icon: FileText, title: "Every claim is cited", body: "A G2 review, a web-search URL, a cluster fact, a verbatim 1-star quote. Click through and check it yourself." },
    { icon: Crosshair, title: "Honest deferrals, not guesses", body: "When the evidence isn't there, you see a visible ⚐ \u201cno grounded source — verify before acting,\u201d never a confident fabrication." },
    { icon: Check, title: "Fact-checked at scale", body: "Two independent fact-check rounds caught and removed fabrications before launch. Zero shipped." },
  ];
  return (
    <section className="relative px-5 sm:px-8 py-16 sm:py-24 overflow-hidden" data-testid="trust-moat-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-[#8B5CF6]/8 blur-[150px]" />
      </div>
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Eyebrow tone="rust"><ShieldCheck size={11} weight="fill" /> The trust moat</Eyebrow>
          <GradHeading className="mt-5 text-3xl sm:text-5xl lg:text-6xl">
            Every number here is one <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">you can verify.</span>
          </GradHeading>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg text-[#5A575B] max-w-2xl mx-auto leading-relaxed">
            Most "AI idea" tools confidently make things up. Prime Pillar's entire design is the opposite — grounded evidence or an honest "we don't know."
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {points.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="relative lum-glass rounded-2xl p-6 sm:p-7 flex items-start gap-4" data-testid={`trust-point-${p.title.split(" ")[1]?.toLowerCase() || "x"}`}>
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-[#8B5CF6]/12 text-[#6D45D6] border border-[#8B5CF6]/25 flex-shrink-0">
                  <Icon size={22} weight="duotone" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#0E0E0E]">{p.title}</h3>
                  <p className="mt-1.5 text-[13.5px] sm:text-sm text-[#3F3D40] leading-relaxed">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


// ============================================================
// TRENDING — live demand, real Google search volume
// ============================================================
const TREND_STATUS = {
  exploding: { label: "Exploding", color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  rising: { label: "Rising", color: "#16a34a", bg: "rgba(22,163,74,0.12)" },
  regular: { label: "Steady", color: "#64748b", bg: "rgba(100,116,139,0.12)" },
  peaked: { label: "Peaked", color: "#d97706", bg: "rgba(217,119,6,0.12)" },
  cooling: { label: "Cooling", color: "#dc2626", bg: "rgba(220,38,38,0.12)" },
  collecting: { label: "Collecting", color: "#a1a1aa", bg: "rgba(161,161,170,0.12)" },
};
const fmtVol = (v) =>
  v == null ? null : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e4 ? `${Math.round(v / 1e3)}K` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`;
const fmtGr = (g) =>
  g == null ? null : `${g < 0 ? "−" : "+"}${Math.abs(g) >= 1000 ? `${Math.round(Math.abs(g) / 100)}×` : `${Math.round(Math.abs(g))}%`}`;

const TrendingSection = ({ onTrending }) => {
  const [topics, setTopics] = useState([]);
  const [total, setTotal] = useState(12547);
  const [sections, setSections] = useState(11);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [f, t] = await Promise.all([
          axios.get(`${API}/trending/topics-board/facets`).catch(() => null),
          axios.get(`${API}/trending/topics-board`, { params: { sort: "growth", page_size: 30 } }).catch(() => null),
        ]);
        if (!alive) return;
        if (f?.data?.total) setTotal(f.data.total);
        if (f?.data?.sections?.length) setSections(f.data.sections.length);
        const picks = (t?.data?.items || [])
          .filter((x) => x.search_volume && x.search_volume >= 5000 && x.growth != null && x.growth > 15)
          .slice(0, 6);
        setTopics(picks);
      } catch (e) { /* keep static fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section id="trending" className="relative px-5 sm:px-8 py-16 sm:py-24 overflow-hidden" data-testid="landing-trending-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-emerald-500/8 blur-[140px]" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#8B5CF6]/8 blur-[140px]" />
      </div>
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <Eyebrow tone="royal"><ChartLineUp size={11} weight="fill" /> Trending · live demand</Eyebrow>
          <GradHeading className="mt-5 text-3xl sm:text-5xl lg:text-6xl">
            See what founders are searching for — <span className="italic font-serif-italic text-[#8B5CF6] [-webkit-text-fill-color:#8B5CF6]">before it's obvious.</span>
          </GradHeading>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg text-[#5A575B] max-w-2xl mx-auto leading-relaxed">
            An Exploding-Topics-scale dataset of <b className="text-[#0E0E0E]">{total.toLocaleString()} trending topics</b> across <b className="text-[#0E0E0E]">{sections} verticals</b> (Health, Beauty, Finance, Food, Fashion, Home…) — ranked by <b className="text-[#0E0E0E]">real Google search volume</b> with 12-month growth, then stamped with a 💎 Emerging / 🔴 Saturated verdict and the funded startups already building it.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.map((t, i) => {
            const s = TREND_STATUS[t.status] || TREND_STATUS.regular;
            return (
              <button
                key={t.slug || i}
                type="button"
                onClick={onTrending}
                data-testid={`landing-trend-card-${i}`}
                className="group text-left bg-white border border-[#E5E8F0] rounded-2xl p-5 hover:border-[#8B5CF6]/50 hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-22px_rgba(139,92,246,0.4)] transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-[#0E0E0E] text-[15px] leading-tight capitalize">{t.name}</h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono-data uppercase tracking-[0.12em] flex-shrink-0" style={{ color: s.color, background: s.bg }}>
                    {s.label}
                  </span>
                </div>
                <div className="mt-4 flex items-baseline gap-5">
                  <div>
                    <div className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{fmtGr(t.growth) || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#9A979C]">Growth (YoY)</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold tabular-nums text-[#0E0E0E]">{fmtVol(t.search_volume) || "—"}<span className="text-sm text-[#9A979C]">/mo</span></div>
                    <div className="text-[10px] uppercase tracking-wider text-[#9A979C]">Searches</div>
                  </div>
                </div>
              </button>
            );
          })}
          {topics.length === 0 && (
            <div className="sm:col-span-2 lg:col-span-3 text-center text-sm text-[#5A575B] py-8" data-testid="landing-trend-loading">
              Loading live trends…
            </div>
          )}
        </div>

        <div className="mt-9 flex flex-col items-center gap-4">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[#5A575B] font-mono-data text-[11px] sm:text-[12px] uppercase tracking-[0.16em]">
            <span>{total.toLocaleString()} topics tracked</span>
            <span className="text-[#C9CBD6]">·</span>
            <span>real Google search volume</span>
            <span className="text-[#C9CBD6]">·</span>
            <span>{sections} verticals</span>
          </div>
          <button
            type="button"
            onClick={onTrending}
            data-testid="landing-trending-cta"
            className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-white shadow-[0_14px_34px_-12px_rgba(139,92,246,0.6)] hover:-translate-y-0.5 transition-transform"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
          >
            Explore Trending <ArrowRight size={17} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};


// ============================================================
// ROOT
// ============================================================
export const LandingPage = ({ onSignUp, onSignIn, onPricing, onDiscover, onEvaluate, onTrending, onBestCountry, onGuide }) => {
  return (
    <div className="min-h-screen bg-[#F1F3F8] text-[#0E0E0E] font-body">
      <LandingNav onSignIn={onSignIn} onSignUp={onSignUp} onDiscover={onDiscover} onEvaluate={onEvaluate} onBestCountry={onBestCountry} onGuide={onGuide} />
      <HeroSection
        onPrimaryPath={onDiscover}
        onSecondaryPath={onSignUp}
      />
      <TwoProductsSection onPrimaryPath={onDiscover} onSecondaryPath={onSignUp} />
      <TrendingSection onTrending={onTrending || onSignUp} />
      <FeaturesSection />
      <ByTheNumbersSection />
      <VsCompetitionSection onCta={onDiscover} />
      <TrustMoatSection />
      <PricingTeaser onPricing={onPricing} />
      <FAQSection />
      <FinalCTASection onPrimaryPath={onDiscover} onSecondaryPath={onSignUp} />
      <BigFooterSection />
    </div>
  );
};
